// Single audit point for everything sent to Google Analytics.
// Hard rule: no personal data ever — no names, emails, phones, free text, or message content.

const safe = (fn, fallback) => {
  try { return fn(); } catch { return fallback; }
};

export const isInternal = () => safe(() => localStorage.getItem("dl_internal") === "1", false);

export const analyticsSuppressed = () =>
  isInternal() || window.location.pathname.startsWith("/admin");

export const analyticsSessionId = () => safe(() => {
  let v = sessionStorage.getItem("dl_sid");
  if (!v) {
    v = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem("dl_sid", v);
  }
  return v;
}, "");

const alreadySeen = (key) => safe(() => {
  const k = `dl_ev_${key}`;
  if (sessionStorage.getItem(k)) return true;
  sessionStorage.setItem(k, "1");
  return false;
}, false);

export const gaEvent = (name, params = {}) => {
  if (analyticsSuppressed()) return;
  try {
    if (typeof window.gtag !== "function") return;
    const clean = {};
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") clean[k] = v;
    }
    if (clean.segment && !clean.audience_segment) clean.audience_segment = clean.segment;
    window.gtag("event", name, clean);
  } catch {}
};

export const gaEventOnce = (name, key, params = {}) => {
  if (analyticsSuppressed()) return;
  if (!alreadySeen(key)) gaEvent(name, params);
};

/* ---- engagement state for qualified_engagement ---- */

const state = {
  steps: new Set(),
  questions: new Set(),
  activeSeconds: 0,
  compliance: false,
  qualified: safe(() => !!sessionStorage.getItem("dl_ev_qualified"), false),
  segment: null,
};

export const setSegment = (s) => { state.segment = s; };

export const engagementCounts = () => ({ questions: state.questions.size, steps: state.steps.size });

const qualify = (trigger) => {
  if (state.qualified) return;
  state.qualified = true;
  safe(() => sessionStorage.setItem("dl_ev_qualified", "1"));
  const payload = {
    trigger, segment: state.segment,
    questions_opened: state.questions.size, loop_steps_opened: state.steps.size,
  };
  gaEvent("qualified_engagement", payload);
  if (!analyticsSuppressed()) {
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event: "qualified_engagement", meta: payload, session_id: analyticsSessionId() }),
      }).catch(() => {});
    } catch {}
  }
};

export const noteLoopStep = (step) => {
  if (state.steps.has(step)) return;
  state.steps.add(step);
  gaEvent("loop_step_open", { step });
  if (state.steps.size >= 3) qualify("loop_steps");
};

export const noteLegalQuestion = (questionId) => {
  if (state.questions.has(questionId)) return;
  state.questions.add(questionId);
  gaEvent("legal_question_open", { question_id: questionId });
  if (state.questions.size >= 2) qualify("legal_questions");
};

export const noteComplianceReached = () => {
  state.compliance = true;
  if (state.activeSeconds >= 90) qualify("engaged_time");
};

let engagementStarted = false;

export const initEngagement = () => {
  if (engagementStarted || analyticsSuppressed()) return;
  engagementStarted = true;
  let lastActivity = Date.now();
  const mark = () => { lastActivity = Date.now(); };
  ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"].forEach((ev) =>
    window.addEventListener(ev, mark, { passive: true }));
  setInterval(() => {
    if (document.visibilityState === "visible" && Date.now() - lastActivity < 15000) {
      state.activeSeconds += 5;
      if (state.activeSeconds >= 90 && state.compliance) qualify("engaged_time");
    }
  }, 5000);
};
